import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  archiveNotification,
  getUnreadCount,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  markNotificationUnread,
} from './api/notifications.api';
import { NotificationsContext, type NotificationsContextValue } from './NotificationsContext';
import type { NotificationFilter, NotificationItem } from './types';
import { useAppRealtimeEvent, useAppRealtimeStatus } from '../../hooks/useAppRealtimeEvents';

const getUnreadDelta = (item?: NotificationItem) => (item && !item.readAt ? 1 : 0);

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const { connected, reconnectCount } = useAppRealtimeStatus();
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasCriticalUnread, setHasCriticalUnread] = useState(false);
  const [latest, setLatest] = useState<NotificationItem[]>([]);
  const [latestFilter, setLatestFilter] = useState<NotificationFilter>('all');
  const [latestLoading, setLatestLoading] = useState(false);
  const [latestLoaded, setLatestLoaded] = useState(false);
  const [version, setVersion] = useState(0);
  const latestOpenRef = useRef(false);
  const latestFilterRef = useRef<NotificationFilter>('all');
  const countPromiseRef = useRef<Promise<void> | null>(null);

  const refreshCount = useCallback(async () => {
    if (countPromiseRef.current) {
      return countPromiseRef.current;
    }

    countPromiseRef.current = getUnreadCount()
      .then((data) => {
        setUnreadCount(data.count);
        setHasCriticalUnread(data.hasCriticalUnread);
      })
      .catch((error: unknown) => {
        console.warn('[notifications] Failed to refresh unread count', error);
      })
      .finally(() => {
        countPromiseRef.current = null;
      });

    return countPromiseRef.current;
  }, []);

  const loadLatest = useCallback(async (filter: NotificationFilter = latestFilterRef.current) => {
    latestFilterRef.current = filter;
    setLatestFilter(filter);
    setLatestLoading(true);
    try {
      const data = await listNotifications({ limit: 10, filter });
      setLatest(data.items);
      setLatestLoaded(true);
    } catch (error) {
      console.warn('[notifications] Failed to load latest notifications', error);
    } finally {
      setLatestLoading(false);
    }
  }, []);

  const setLatestOpen = useCallback(
    (open: boolean) => {
      latestOpenRef.current = open;
      if (open) {
        void loadLatest(latestFilterRef.current);
      }
    },
    [loadLatest]
  );

  const markRead = useCallback(
    async (publicId: string) => {
      const currentItem = latest.find((item) => item.publicId === publicId);
      const previousLatest = latest;
      const previousCount = unreadCount;
      const previousCritical = hasCriticalUnread;
      const readAt = new Date().toISOString();

      setLatest((items) => {
        const next =
          latestFilterRef.current === 'unread'
            ? items.filter((item) => item.publicId !== publicId)
            : items.map((item) => (item.publicId === publicId ? { ...item, readAt } : item));

        if (currentItem?.severity === 'CRITICAL') {
          setHasCriticalUnread(next.some((item) => item.severity === 'CRITICAL' && !item.readAt));
        }

        return next;
      });
      if (getUnreadDelta(currentItem)) {
        setUnreadCount((count) => Math.max(0, count - 1));
      }

      try {
        const response = await markNotificationRead(publicId);
        setLatest((items) =>
          items.map((item) =>
            item.publicId === publicId ? { ...item, readAt: response.readAt } : item
          )
        );
        if (!currentItem || currentItem.severity === 'CRITICAL') {
          void refreshCount();
        }
      } catch (error) {
        setUnreadCount(previousCount);
        setHasCriticalUnread(previousCritical);
        setLatest(previousLatest);
        throw error;
      }
    },
    [hasCriticalUnread, latest, refreshCount, unreadCount]
  );

  const markUnread = useCallback(
    async (publicId: string) => {
      const currentItem = latest.find((item) => item.publicId === publicId);
      const previousCount = unreadCount;
      const previousCritical = hasCriticalUnread;

      setLatest((items) =>
        items.map((item) => (item.publicId === publicId ? { ...item, readAt: null } : item))
      );
      if (currentItem?.readAt) {
        setUnreadCount((count) => Math.min(100, count + 1));
      }
      if (currentItem?.severity === 'CRITICAL') {
        setHasCriticalUnread(true);
      }

      try {
        await markNotificationUnread(publicId);
      } catch (error) {
        setUnreadCount(previousCount);
        setHasCriticalUnread(previousCritical);
        setLatest((items) =>
          items.map((item) => (item.publicId === publicId && currentItem ? currentItem : item))
        );
        throw error;
      }
    },
    [hasCriticalUnread, latest, unreadCount]
  );

  const markAllRead = useCallback(async () => {
    const previousLatest = latest;
    const previousCount = unreadCount;
    const previousCritical = hasCriticalUnread;
    const readAt = new Date().toISOString();

    setLatest((items) =>
      latestFilterRef.current === 'unread'
        ? []
        : items.map((item) => ({ ...item, readAt: item.readAt ?? readAt }))
    );
    setUnreadCount(0);
    setHasCriticalUnread(false);

    try {
      await markAllNotificationsRead();
      setVersion((value) => value + 1);
    } catch (error) {
      setLatest(previousLatest);
      setUnreadCount(previousCount);
      setHasCriticalUnread(previousCritical);
      throw error;
    }
  }, [hasCriticalUnread, latest, unreadCount]);

  const archive = useCallback(
    async (publicId: string) => {
      const currentItem = latest.find((item) => item.publicId === publicId);
      const previousLatest = latest;
      const previousCount = unreadCount;
      const previousCritical = hasCriticalUnread;

      setLatest((items) => items.filter((item) => item.publicId !== publicId));
      if (getUnreadDelta(currentItem)) {
        setUnreadCount((count) => Math.max(0, count - 1));
      }
      if (currentItem?.severity === 'CRITICAL' && !currentItem.readAt) {
        setHasCriticalUnread(false);
      }

      try {
        await archiveNotification(publicId);
        setVersion((value) => value + 1);
      } catch (error) {
        setLatest(previousLatest);
        setUnreadCount(previousCount);
        setHasCriticalUnread(previousCritical);
        throw error;
      }
    },
    [hasCriticalUnread, latest, unreadCount]
  );

  useAppRealtimeEvent('notification.created', () => {
    void refreshCount();
    setVersion((value) => value + 1);
    if (latestOpenRef.current) {
      void loadLatest(latestFilterRef.current);
    }
  });

  useEffect(() => {
    void refreshCount();
  }, [refreshCount, reconnectCount]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void refreshCount();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refreshCount]);

  useEffect(() => {
    if (connected) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      void refreshCount();
    }, 60000);

    return () => window.clearInterval(intervalId);
  }, [connected, refreshCount]);

  const value = useMemo<NotificationsContextValue>(
    () => ({
      unreadCount,
      hasCriticalUnread,
      latest,
      latestFilter,
      latestLoading,
      latestLoaded,
      version,
      refreshCount,
      loadLatest,
      setLatestOpen,
      markRead,
      markUnread,
      markAllRead,
      archive,
    }),
    [
      archive,
      hasCriticalUnread,
      latest,
      latestFilter,
      latestLoaded,
      latestLoading,
      loadLatest,
      markAllRead,
      markRead,
      markUnread,
      refreshCount,
      setLatestOpen,
      unreadCount,
      version,
    ]
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}
